import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Map as MapGL } from 'react-map-gl/maplibre';
import DeckGL from '@deck.gl/react';
import { GeoJsonLayer } from '@deck.gl/layers';
import { 
    FiTrendingUp, 
    FiUsers, 
    FiTarget, 
    FiBarChart2, 
    FiMessageSquare, 
    FiCheckCircle,  
    FiXCircle,        
    FiShield,
    FiX,
    FiSearch,
    FiFilter,
    FiTrash2,
    FiChevronDown,
    FiChevronUp,
    FiMap,
    FiPieChart 
} from 'react-icons/fi';

const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

const DEFAULT_VIEW_STATE = {
    longitude: -0.96,
    latitude: 51.05,
    zoom: 10,
    pitch: 0,
    bearing: 0,
};

// --- UK POLITICAL PARTY COLORS (Accurate Hex/RGBA with 200 Alpha) ---
const PARTY_COLORS = {
    Conservative: [0, 135, 220, 200],      // #0087DC - Blue
    Labour: [228, 0, 59, 200],             // #E4003B - Red
    "Liberal Democrats": [250, 166, 26, 200], // #FAA61A - Orange/Gold
    Green: [106, 176, 35, 200],            // #6AB023 - Green
    Reform: [18, 182, 207, 200],           // #12B6CF - Teal
    SNP: [255, 249, 93, 200],              // Yellow
    Other: [100, 100, 110, 200],
    DEFAULT: [100, 100, 110, 200],
};

// --- TIER COLORS (Strictly adhered to for Campaign Status) ---
const TIER_COLORS = {
    GAIN_PRIORITY: [234, 179, 8, 200],    
    GAIN_TARGET: [250, 204, 21, 200],     
    DEFEND_CRITICAL: [220, 38, 38, 200],  
    DEFEND_URGENT: [249, 115, 22, 200],   
    DEFEND_PRIORITY: [14, 165, 233, 200], 
    LONG_TERM_TARGET: [96, 165, 250, 200],
    SAFE_HOLD: [34, 197, 94, 200],        
    UNWINNABLE: [71, 85, 105, 200],       
    WATCH_LIST: [168, 85, 247, 200],      
    NO_CONTEST: [168, 85, 247, 200],      
    DEFAULT: [100, 100, 110, 200],        
};

// Logic to determine Campaign Status based on the "Most Critical" ward present
const TIER_PRECEDENCE = [
    'DEFEND_CRITICAL',
    'GAIN_PRIORITY',
    'DEFEND_URGENT',
    'GAIN_TARGET',
    'DEFEND_PRIORITY',
    'LONG_TERM_TARGET',
    'SAFE_HOLD',
    'WATCH_LIST',
    'UNWINNABLE',
    'NO_CONTEST'
];

// --- ARCHETYPE INTELLIGENCE DB ---
const ARCHETYPE_DETAILS = {
    "URBAN_PROGRESSIVE_CORE": {
        party: "Green",
        leaning: "Progressive Left",
        concerns: ["Social Justice", "Climate Change", "Economic Inequality"],
        bio: "This individual tends to be found in high-density urban centres and is overrepresented among degree-educated professionals. Their worldview is defined by a strong progressive-left, anti-authoritarian, and egalitarian disposition. They are highly engaged, principled, and possess a high degree of political efficacy.",
        neighbors: { prog: "SOCIAL_JUSTICE_WARRIOR", cons: "SOFT_LEFT_LIBERAL" }
    },
    "CLIMATE_ACTIVIST": {
        party: "Green",
        leaning: "Progressive Left (Single-Issue)",
        concerns: ["Climate Change", "Environment", "Transport"],
        bio: "This highly educated professional has a political identity primarily defined by a single, overriding issue: the environment. They are passionate, highly open to new ideas, and disciplined in their activism. Psychologically, they are driven by high empathy and climate-related anxiety.",
        neighbors: { prog: "SOCIAL_JUSTICE_WARRIOR", cons: "URBAN_PROGRESSIVE_CORE" }
    },
    "SOCIAL_JUSTICE_WARRIOR": {
        party: "Labour",
        leaning: "Progressive Left (Radical)",
        concerns: ["Social Justice", "Economic Inequality", "Discrimination"],
        bio: "This archetype represents a highly engaged, activist egalitarian. Their political activity is an extension of their core moral convictions, defined by an extremely strong anti-hierarchical and egalitarian worldview. They are vocal, community-oriented, and deeply cynical about existing institutions.",
        neighbors: { prog: "CLIMATE_ACTIVIST", cons: "URBAN_PROGRESSIVE_CORE" }
    },
    "SOFT_LEFT_LIBERAL": {
        party: "Labour",
        leaning: "Centre-Left",
        concerns: ["Healthcare", "Education", "Economy"],
        bio: "This moderate, professional, and family-oriented individual represents the pragmatic centre-left. Unlike more radical progressives, they respect institutions and seek incremental reform. Their primary concerns are core public services, namely the NHS and education.",
        neighbors: { prog: "URBAN_PROGRESSIVE_CORE", cons: "CIVIC_PRAGMATIST" }
    },
    "CIVIC_PRAGMATIST": {
        party: "Liberal Democrats",
        leaning: "Centrist / Centre-Left",
        concerns: ["Local Services", "Healthcare", "Education"],
        bio: "This moderate, institution-trusting individual is defined by a strong sense of civic duty and community involvement. They are pragmatic, cooperative, and respect tradition more than their liberal counterparts. They are primarily concerned with the effective delivery of local services.",
        neighbors: { prog: "SOFT_LEFT_LIBERAL", cons: "MIDDLE_BRITAIN_LEFT" }
    },
    "MIDDLE_BRITAIN_LEFT": {
        party: "Labour",
        leaning: "Centre-Left",
        concerns: ["Cost of Living", "Healthcare", "Economy"],
        bio: "Representing the heart of 'Middle Britain', this individual is a mainstream, left-of-centre voter, typically a family-oriented homeowner. Their political views are guided by pragmatism rather than strong ideology, dominated by immediate economic pressures like the cost of living.",
        neighbors: { prog: "CIVIC_PRAGMATIST", cons: "WELL_OFF_TRADITIONALIST" }
    },
    "PROFESSIONAL_MODERATE": {
        party: "Liberal Democrats",
        leaning: "Centrist / Liberal",
        concerns: ["Economy", "Education", "Healthcare"],
        bio: "This career-focused, affluent, and highly organised individual holds a meritocratic and individualistic worldview. They are socially liberal but economically pragmatic, prioritising a stable economy above all else. They are the quintessential centrist swing voter.",
        neighbors: { prog: "SOFT_LEFT_LIBERAL", cons: "ESTABLISHED_LIBERAL_CORE" }
    },
    "WELL_OFF_TRADITIONALIST": {
        party: "Conservative",
        leaning: "Centre-Right (Traditional)",
        concerns: ["Economy", "Healthcare", "Local Services"],
        bio: "This affluent, older professional is secure and has high trust in British institutions. Their worldview blends 'One Nation' social liberalism with economic conservatism, rooted in a respect for tradition. They are currently torn between traditional loyalty and liberal values.",
        neighbors: { prog: "ESTABLISHED_LIBERAL_CORE", cons: "OPTIMISTIC_CONTENTMENT" }
    },
    "ESTABLISHED_LIBERAL_CORE": {
        party: "Liberal Democrats",
        leaning: "Centrist / Liberal",
        concerns: ["Economy", "Europe", "Education"],
        bio: "This affluent, senior professional is confident in their success and holds a cosmopolitan, meritocratic worldview. They are internationalist, with a key interest in Europe, and prioritise economic stability. They are the absolute bedrock of the Liberal Democrat vote.",
        neighbors: { prog: "PROFESSIONAL_MODERATE", cons: "WELL_OFF_TRADITIONALIST" }
    },
    "OPTIMISTIC_CONTENTMENT": {
        party: "Conservative",
        leaning: "Centre-Right (Traditional)",
        concerns: ["Healthcare", "Local Services", "Economy"],
        bio: "This archetype represents an older, retired, and financially secure individual who is deeply content with their life. They are defined by optimism, low anxiety, and very high trust in institutions. They generally vote for status-quo stability.",
        neighbors: { prog: "WELL_OFF_TRADITIONALIST", cons: "DISENGAGED_TRADITIONALIST_CONSERVATIVE" }
    },
    "APOLITICAL_CENTRIST": {
        party: "Other",
        leaning: "Disengaged",
        concerns: ["Cost of Living", "Housing", "Jobs"],
        bio: "This individual is part of a broad, disengaged cluster defined by high cynicism and a profound sense of powerlessness. Their concerns are immediate and personal. They are politically homeless and extremely hard to motivate, often abstaining.",
        neighbors: { prog: "CALM_PERSISTENCE", cons: "PASSIVE_CENTRE" }
    },
    "PASSIVE_CENTRE": {
        party: "Labour",
        leaning: "Disengaged / Centrist",
        concerns: ["Cost of Living", "Local Services", "Healthcare"],
        bio: "This individual represents a family-focused, passive segment. They are not necessarily cynical but are politically withdrawn and feel powerless. Their concerns are practical and immediate, focusing on the cost of living and local services.",
        neighbors: { prog: "APOLITICAL_CENTRIST", cons: "HARD_PRESSED_ANXIETY" }
    },
    "CALM_PERSISTENCE": {
        party: "Labour",
        leaning: "Disengaged / Centrist",
        concerns: ["Housing", "Cost of Living", "Jobs"],
        bio: "This young, urban, and digitally-native individual is part of a diverse 'disengaged' cluster. Their focus is individualistic, centred on personal experience and navigating a precarious economy. Their primary concerns are barriers to their lifestyle like housing costs.",
        neighbors: { prog: "URBAN_PROGRESSIVE_CORE", cons: "APOLITICAL_CENTRIST" }
    },
    "LEFT_BEHIND_PATRIOT": {
        party: "Reform",
        leaning: "Populist Right",
        concerns: ["Immigration", "Economy", "Jobs"],
        bio: "This older, working-class individual feels economically abandoned and culturally threatened. They are a 'cross-pressured' voter: culturally traditional but economically precarious. This results in high volatility between Reform UK and Labour.",
        neighbors: { prog: "HARD_PRESSED_ANXIETY", cons: "LOYAL_NATIONAL" }
    },
    "LOYAL_NATIONAL": {
        party: "Conservative",
        leaning: "Authoritarian Right",
        concerns: ["Patriotism", "Tradition", "Immigration"],
        bio: "This older, working-class individual is defined by an extremely strong sense of patriotism and respect for traditional values. Their worldview is shaped by in-group loyalty and a desire for purity and order. They are motivated by values, not economics.",
        neighbors: { prog: "LEFT_BEHIND_PATRIOT", cons: "BACKBONE_CONSERVATIVE_PATRIOT" }
    },
    "BACKBONE_CONSERVATIVE_PATRIOT": {
        party: "Conservative",
        leaning: "Authoritarian Right",
        concerns: ["Tradition", "Law and Order", "Immigration"],
        bio: "This older, retired individual is the epitome of a 'duty-bound' conservative, with extremely high respect for authority and national tradition. They seek certainty and are motivated by a strong sense of national pride.",
        neighbors: { prog: "LOYAL_NATIONAL", cons: "WELL_OFF_TRADITIONALIST_CONSERVATIVE" }
    },
    "DISENGAGED_TRADITIONALIST_PATRIOT": {
        party: "Reform",
        leaning: "Right-Leaning / Disengaged",
        concerns: ["Tradition", "Local Services", "Economy"],
        bio: "This individual holds strong traditional values but is politically withdrawn and feels powerless. They are economically stressed and distrustful of a system they feel has left them behind. They are a key activation target for Reform UK.",
        neighbors: { prog: "PASSIVE_CENTRE", cons: "DISENGAGED_TRADITIONALIST_CONSERVATIVE" }
    },
    "WELL_OFF_TRADITIONALIST_CONSERVATIVE": {
        party: "Conservative",
        leaning: "Authoritarian Right",
        concerns: ["Tradition", "Law and Order", "Economy"],
        bio: "This affluent, retired, and highly disciplined individual represents the established, wealthy right. They believe strongly in hierarchy, tradition, and authority. They are primarily concerned with upholding the status quo and a conservative economy.",
        neighbors: { prog: "BACKBONE_CONSERVATIVE_PATRIOT", cons: "BACKBONE_CONSERVATIVE_CORE" }
    },
    "BACKBONE_CONSERVATIVE_CORE": {
        party: "Conservative",
        leaning: "Authoritarian Right",
        concerns: ["Tradition", "Law and Order", "Immigration"],
        bio: "This is the most traditional, conventional, and dutiful archetype. Their entire worldview is built on an extremely high respect for tradition, authority, and national loyalty. They are the most reliable, high-turnout conservative voters.",
        neighbors: { prog: "WELL_OFF_TRADITIONALIST_CONSERVATIVE", cons: "BACKBONE_CONSERVATIVE_PATRIOT" }
    },
    "DISENGAGED_TRADITIONALIST_CONSERVATIVE": {
        party: "Conservative",
        leaning: "Right-Leaning / Disengaged",
        concerns: ["Tradition", "Local Services", "Healthcare"],
        bio: "This older, retired individual holds strong conservative values but is politically passive. They respect authority but have lower energy and engagement. Their concerns are a mix of values (tradition) and practicalities (services).",
        neighbors: { prog: "DISENGAGED_TRADITIONALIST_PATRIOT", cons: "OPTIMISTIC_CONTENTMENT" }
    },
    "DISSENTING_DISRUPTOR_CORE": {
        party: "Reform",
        leaning: "Populist Right / Anti-Establishment",
        concerns: ["Anti-Establishment", "Immigration", "Sovereignty"],
        bio: "This highly-energised, anti-establishment populist is defined by deep anger and profound cynicism. They believe the system is corrupt and are driven by an 'us vs. them' worldview. They are the motivated, angry base of Reform UK.",
        neighbors: { prog: "LONG_TERM_DESPAIR", cons: "LEFT_BEHIND_PATRIOT" }
    },
    "LONG_TERM_DESPAIR": {
        party: "Reform",
        leaning: "Disengaged / Anti-Establishment",
        concerns: ["Jobs", "Economy", "Benefits"],
        bio: "This archetype is defined by a profound sense of pessimism and economic despair. They feel completely abandoned and failed by the system. Their concerns are entirely economic. Their deep frustration makes them a target for anti-establishment messaging.",
        neighbors: { prog: "HARD_PRESSED_ANXIETY", cons: "DISSENTING_DISRUPTOR_CORE" }
    },
    "HARD_PRESSED_ANXIETY": {
        party: "Labour",
        leaning: "Economically Populist / Volatile",
        concerns: ["Cost of Living", "Housing", "Jobs"],
        bio: "This individual is in 'survival mode', defined by very high anxiety related to the cost of living. They feel the system is unfair. This group is 'cross-pressured' between Labour's economic promises and populist anti-establishment messages.",
        neighbors: { prog: "PASSIVE_CENTRE", cons: "LONG_TERM_DESPAIR" }
    }
};

const calculateBounds = (features) => {
    if (!features || features.length === 0) return null;
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;

    features.forEach(feature => {
        if (!feature.geometry || !feature.geometry.coordinates) return;
        const coords = feature.geometry.type === 'MultiPolygon' 
            ? feature.geometry.coordinates.flat(2) 
            : feature.geometry.coordinates.flat(1);
        coords.forEach(coord => {
            const [lng, lat] = coord;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        });
    });

    if (!isFinite(minLng) || !isFinite(minLat)) return null;
    const longitude = (minLng + maxLng) / 2;
    const latitude = (minLat + maxLat) / 2;
    const maxDiff = Math.max(maxLng - minLng, maxLat - minLat);
    
    let zoom = 11;
    if (maxDiff < 0.04) zoom = 13;
    else if (maxDiff < 0.1) zoom = 12;
    else if (maxDiff < 0.5) zoom = 10;
    else zoom = 9;

    return { longitude, latitude, zoom, pitch: 0, bearing: 0 };
};

// Tier-matched glow colours for the badge drop-shadow
const TIER_GLOW = {
    GAIN_PRIORITY:    'rgba(234,179,8,0.5)',
    GAIN_TARGET:      'rgba(250,204,21,0.45)',
    DEFEND_CRITICAL:  'rgba(220,38,38,0.55)',
    DEFEND_URGENT:    'rgba(249,115,22,0.5)',
    DEFEND_PRIORITY:  'rgba(14,165,233,0.5)',
    LONG_TERM_TARGET: 'rgba(96,165,250,0.45)',
    SAFE_HOLD:        'rgba(34,197,94,0.5)',
    UNWINNABLE:       'rgba(71,85,105,0.4)',
    WATCH_LIST:       'rgba(168,85,247,0.5)',
    NO_CONTEST:       'rgba(168,85,247,0.5)',
    DEFAULT:          'rgba(100,116,139,0.4)',
};

const HeaderStats = ({ selectedWardData, summaryStats, authorityName, vw = 1400, onHeightChange }) => {
    if (!summaryStats) return <div className="p-4 bg-gray-900 text-white">Loading...</div>;

    const data = selectedWardData ? {
        title: selectedWardData.ward_name,
        tier: selectedWardData.strategic_priority?.tier || selectedWardData.competitive_tier,
        stat1: { 
            label: 'Winability', 
            value: `${(( (selectedWardData.winability?.score || selectedWardData.winability_score) || 0) * 100).toFixed(1)}%` 
        },
        stat2: { label: 'Margin', value: selectedWardData.swing_analysis?.margin_votes || selectedWardData.current_margin || 0 },
        stat3: { label: 'Persuadable', value: selectedWardData.voter_targeting?.persuadable_voters || selectedWardData.voter_segments?.persuadable || 0 },
    } : {
        title: `${authorityName || 'Authority'} Overview`,
        tier: summaryStats.campaignTier, 
        stat1: { label: 'Avg Winability', value: `${(summaryStats.avgWinability * 100).toFixed(1)}%` },
        stat2: { label: 'Gain Targets', value: summaryStats.gainTargets },
        stat3: { label: 'Defend Targets', value: summaryStats.defendTargets },
        // NEW: Allocation Stat for Header
        stat4: { label: 'High Priority', value: summaryStats.highPriorityResources || 0 }, 
    };

    const TIER_STYLES = {
        GAIN_PRIORITY: 'bg-yellow-500 border-yellow-400 text-yellow-900',
        GAIN_TARGET: 'bg-yellow-400 border-yellow-300 text-yellow-900',
        DEFEND_CRITICAL: 'bg-red-600 border-red-500 text-white',
        DEFEND_URGENT: 'bg-orange-500 border-orange-400 text-white',
        DEFEND_PRIORITY: 'bg-sky-500 border-sky-400 text-white',
        LONG_TERM_TARGET: 'bg-blue-400 border-blue-300 text-white',
        SAFE_HOLD: 'bg-green-500 border-green-400 text-white',
        UNWINNABLE: 'bg-slate-600 border-slate-500 text-white',
        WATCH_LIST: 'bg-purple-500 border-purple-400 text-white',
        NO_CONTEST: 'bg-purple-500 border-purple-400 text-white',
        DEFAULT: 'bg-gray-700 border-gray-600 text-white'
    };

    const tierClass = TIER_STYLES[data.tier] || TIER_STYLES.DEFAULT;

    // Filter out undefined stats
    const statsToShow = [data.stat1, data.stat2, data.stat3, data.stat4].filter(s => s && s.value !== undefined);

    // Breakpoints inside the component (driven by real vw passed from parent)
    // isMedium: tablet / small desktop — header wraps, sidebar narrows
    const isMedium = vw >= 640 && vw < 1380;

    // Per-stat colour tokens — background tint, border, glow, label colour
    const getStatStyle = (stat) => {
        const label = stat.label.toLowerCase();
        const raw   = parseFloat(String(stat.value).replace(/[^0-9.\-]/g, ''));

        // Winability — green/amber/red gradient
        if (label.includes('winab')) {
            if (raw >= 70) return { bg:'rgba(34,197,94,0.13)',  border:'rgba(34,197,94,0.3)',  glow:'rgba(34,197,94,0.08)',  lbl:'rgb(134,239,172)' };
            if (raw >= 40) return { bg:'rgba(234,179,8,0.13)',  border:'rgba(234,179,8,0.3)',  glow:'rgba(234,179,8,0.08)',  lbl:'rgb(253,224,71)'  };
            return              { bg:'rgba(239,68,68,0.13)',   border:'rgba(239,68,68,0.3)',   glow:'rgba(239,68,68,0.08)',   lbl:'rgb(252,165,165)' };
        }
        // Gain targets — yellow/opportunity
        if (label.includes('gain')) {
            return { bg:'rgba(234,179,8,0.13)', border:'rgba(234,179,8,0.3)', glow:'rgba(234,179,8,0.08)', lbl:'rgb(253,224,71)' };
        }
        // Defend targets — orange/urgency
        if (label.includes('defend')) {
            return { bg:'rgba(249,115,22,0.13)', border:'rgba(249,115,22,0.3)', glow:'rgba(249,115,22,0.08)', lbl:'rgb(253,186,116)' };
        }
        // High priority / resource — red/critical
        if (label.includes('priority') || label.includes('resource')) {
            return { bg:'rgba(239,68,68,0.13)', border:'rgba(239,68,68,0.3)', glow:'rgba(239,68,68,0.08)', lbl:'rgb(252,165,165)' };
        }
        // Margin — green if positive, red if negative
        if (label.includes('margin')) {
            if (raw > 0) return { bg:'rgba(34,197,94,0.13)',  border:'rgba(34,197,94,0.3)',  glow:'rgba(34,197,94,0.08)',  lbl:'rgb(134,239,172)' };
            return              { bg:'rgba(239,68,68,0.13)',  border:'rgba(239,68,68,0.3)',  glow:'rgba(239,68,68,0.08)',  lbl:'rgb(252,165,165)' };
        }
        // Persuadable — purple/targeting
        if (label.includes('persuad')) {
            return { bg:'rgba(168,85,247,0.13)', border:'rgba(168,85,247,0.3)', glow:'rgba(168,85,247,0.08)', lbl:'rgb(216,180,254)' };
        }
        // Fallback — neutral glass
        return { bg:'rgba(6,182,212,0.12)', border:'rgba(6,182,212,0.25)', glow:'rgba(6,182,212,0.08)', lbl:'rgba(103,232,249,0.7)' };
    };

    // Glassmorphic desktop header — floats over the map
    // isMedium: two-row layout so nothing overflows on tablet
    const badge = (
        <div className={`pointer-events-auto shrink-0 px-4 py-2.5 rounded-2xl backdrop-blur-sm shadow-2xl ${tierClass}`}
            style={{boxShadow:`0 0 24px ${TIER_GLOW[data.tier] || 'rgba(100,116,139,0.4)'}`}}>
            <p className={`font-bold leading-none ${isMedium ? 'text-xs' : 'text-sm'}`}>{data.tier?.replace(/_/g, ' ')}</p>
            <p className="text-xs uppercase opacity-70 mt-0.5">Status</p>
        </div>
    );

    const titlePill = (
        <div className="pointer-events-auto flex-1 min-w-0 px-4 py-2.5 rounded-2xl backdrop-blur-md shadow-xl"
            style={{background:'rgba(10,15,28,0.82)', border:'1px solid rgba(255,255,255,0.08)'}}>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-0.5 leading-none">Campaign</p>
            <h1 className={`font-bold text-white truncate leading-tight ${isMedium ? 'text-base' : 'text-xl'}`}>{data.title}</h1>
        </div>
    );

    const statCards = (
        <div className={`pointer-events-auto flex gap-1.5 ${isMedium ? 'w-full' : 'shrink-0'}`}>
            {statsToShow.map((stat, i) => {
                const s = getStatStyle(stat);
                return (
                    <div key={i} className={`rounded-2xl backdrop-blur-md shadow-xl text-center ${isMedium ? 'flex-1 px-2 py-2' : 'px-4 py-2.5 min-w-[100px]'}`}
                        style={{background:s.bg, border:`1px solid ${s.border}`, boxShadow:`0 4px 24px ${s.glow}`}}>
                        <p className={`font-bold text-white leading-none ${isMedium ? 'text-lg' : 'text-xl'}`}>{stat.value}</p>
                        <p className={`uppercase mt-0.5 leading-none font-semibold ${isMedium ? 'text-[9px]' : 'text-xs'}`} style={{color:s.lbl}}>{stat.label}</p>
                    </div>
                );
            })}
        </div>
    );

    const headerRef = useRef(null);
    useEffect(() => {
        if (!isMedium || !headerRef.current || !onHeightChange) return;
        const ro = new ResizeObserver(([e]) => onHeightChange(e.contentRect.height));
        ro.observe(headerRef.current);
        onHeightChange(headerRef.current.offsetHeight);
        return () => ro.disconnect();
    }, [isMedium]);

    if (isMedium) {
        // Two-row layout: row1 = [badge + title pill], row2 = stat cards spanning full width
        return (
            <div ref={headerRef} className="absolute top-3 left-3 right-3 z-10 flex flex-col gap-1.5 pointer-events-none">
                <div className="flex items-stretch gap-2">
                    {badge}
                    {titlePill}
                </div>
                {statCards}
            </div>
        );
    }

    return (
        <div className="absolute top-4 left-4 right-4 z-10 flex items-center gap-3 pointer-events-none">
            {badge}
            {titlePill}
            {statCards}
        </div>
    );
};

// --- Archetype Modal Component ---
const ArchetypeModal = ({ isOpen, onClose, data, wardName }) => {
    if (!isOpen || !data) return null;

    const partyColor = PARTY_COLORS[data.party] || PARTY_COLORS.DEFAULT;
    const borderColor = `rgba(${partyColor[0]}, ${partyColor[1]}, ${partyColor[2]}, 1)`;
    const bgColor = `rgba(${partyColor[0]}, ${partyColor[1]}, ${partyColor[2]}, 0.15)`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div 
                className="w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden relative"
                style={{background:'rgba(8,12,24,0.92)', border:'1px solid rgba(255,255,255,0.1)', backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)'}}
                onClick={e => e.stopPropagation()}
            >
                <div className="h-2 w-full" style={{ backgroundColor: borderColor }} />
                
                <div className="p-6">
                    <button 
                        onClick={onClose} 
                        className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                    >
                        <FiX size={24} />
                    </button>

                    <div className="mb-4">
                        <p className="text-gray-400 text-sm uppercase tracking-wider font-semibold mb-1">Dominant Archetype in {wardName}</p>
                        <h2 className="text-3xl font-bold text-white mb-2">{data.name}</h2>
                        <span 
                            className="inline-block px-3 py-1 rounded-full text-xs font-bold border"
                            style={{ backgroundColor: bgColor, borderColor: borderColor, color: 'white' }}
                        >
                            {data.party} • {data.leaning}
                        </span>
                    </div>

                    <div className="prose prose-invert mb-6">
                        <p className="text-gray-300 text-sm leading-relaxed">
                            {data.bio}
                        </p>
                    </div>

                    <div>
                        <h4 className="text-white font-semibold mb-2 flex items-center text-sm uppercase">
                            <FiTarget className="mr-2 text-cyan-400" /> Key Concerns
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {data.concerns.map((concern, i) => (
                                <span key={i} className="px-3 py-1 rounded-full text-xs text-gray-200" style={{background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)'}}>
                                    {concern}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const InfoPanel = ({ ward, campaignOverview, onClose }) => {
    
    // Helper Components
    const DataRow = ({ label, value, valueClass = 'text-white font-bold' }) => (
        <div className="flex justify-between items-start py-2 border-b border-gray-700/50">
            <span className="text-sm text-gray-400 shrink-0">{label}</span>
            <span className={`text-sm text-right pl-4 ${valueClass}`}>{value}</span>
        </div>
    );

    const Pill = ({ text, tier }) => {
        const styles = {
            GAIN_PRIORITY: 'bg-yellow-500 border-yellow-400 text-yellow-900',
            GAIN_TARGET: 'bg-yellow-400 border-yellow-300 text-yellow-900',
            DEFEND_CRITICAL: 'bg-red-600 border-red-500 text-white',
            DEFEND_URGENT: 'bg-orange-500 border-orange-400 text-white',
            DEFEND_PRIORITY: 'bg-sky-500 border-sky-400 text-white',
            LONG_TERM_TARGET: 'bg-blue-400 border-blue-300 text-white',
            SAFE_HOLD: 'bg-green-500 border-green-400 text-white',
            UNWINNABLE: 'bg-slate-600 border-slate-500 text-white',
            WATCH_LIST: 'bg-purple-500 border-purple-400 text-white',
            NO_CONTEST: 'bg-purple-500 border-purple-400 text-white',
            DEFAULT: 'bg-gray-700 border-gray-600 text-white',
        };
        const glowMap = {
            GAIN_PRIORITY:'rgba(234,179,8,0.4)',GAIN_TARGET:'rgba(250,204,21,0.35)',
            DEFEND_CRITICAL:'rgba(220,38,38,0.45)',DEFEND_URGENT:'rgba(249,115,22,0.4)',
            DEFEND_PRIORITY:'rgba(14,165,233,0.4)',LONG_TERM_TARGET:'rgba(96,165,250,0.35)',
            SAFE_HOLD:'rgba(34,197,94,0.4)',UNWINNABLE:'rgba(71,85,105,0.3)',
            WATCH_LIST:'rgba(168,85,247,0.4)',NO_CONTEST:'rgba(168,85,247,0.4)',
            DEFAULT:'rgba(100,116,139,0.3)',
        };
        return (
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${styles[tier] || styles.DEFAULT}`}
                style={{boxShadow:`0 0 12px ${glowMap[tier] || glowMap.DEFAULT}`}}>
                {text?.replace(/_/g, ' ')}
            </span>
        );
    };

    const getCssColor = (c, a=1) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;

    // ------------------------------------
    // PREPARE DATA: EITHER WARD OR CAMPAIGN
    // ------------------------------------
    const isWardMode = !!ward;
    const data = isWardMode ? ward.properties : campaignOverview;

    if (!data) {
        return (
            <div className="p-6 text-center text-gray-400 h-full flex flex-col items-center justify-center">
                <FiTarget size={48} className="mb-4 opacity-20" />
                <h2 className="text-xl font-semibold text-white mb-2">No Data Available</h2>
            </div>
        );
    }

    // --- MAPPING LOGIC TO NORMALIZE WARD VS CAMPAIGN DATA ---
    const displayProps = {
        title: isWardMode ? data.ward_name : 'Campaign Overview',
        icon: isWardMode ? null : <FiMap size={24} className="text-gray-400" />,
        
        // Strategic Overview
        statusTier: isWardMode ? (data.strategic_priority?.tier || data.competitive_tier) : data.strategic_priority.tier,
        strategy: isWardMode ? data.strategic_priority?.campaign_strategy : data.strategic_priority.campaign_strategy,
        primaryObjective: isWardMode 
            ? (data.recommendations?.campaign?.[0] || 'N/A')
            : data.strategic_priority.primary_objective,
        
        // NEW: Resource Allocation Breakdown (Campaign Mode Only)
        resourceCounts: !isWardMode ? data.resource_allocation_counts : null,

        // Win Path Analysis
        winabilityScore: isWardMode 
            ? ((data.winability?.score || 0) * 100).toFixed(1) 
            : (data.winability.avg_score * 100).toFixed(1),
        marginVotes: isWardMode 
            ? (data.swing_analysis?.margin_votes || 0) 
            : data.winability.aggregate_margin_votes, // Calculated in useEffect
        swingRequired: isWardMode 
            ? (data.swing_analysis?.swing_required_pct || 0) 
            : data.winability.avg_swing_required,
        persuadablePool: isWardMode 
            ? (data.voter_targeting?.persuadable_voters || 0) 
            : data.voter_targeting.total_persuadable,
        squeezePool: isWardMode 
            ? (data.voter_targeting?.squeeze_opportunities?.Squeeze_Total_Pool_BQ || 0) 
            : data.voter_targeting.total_squeeze,
        
        opportunities: isWardMode 
            ? (data.winability?.opportunity_factors || []) 
            : data.winability.opportunities,
        risks: isWardMode 
            ? (data.winability?.risk_factors || []) 
            : data.winability.risks,

        // Historic
        lastElectionWinner: isWardMode ? (data.last_election_results?.winner) : data.historic_voting.overall_winner,
        voteData: isWardMode ? (data.last_election_results?.party_votes || {}) : data.historic_voting.total_votes,

        // Archetypes
        archetypes: isWardMode 
            ? (data.voter_targeting?.priority_archetypes || []) 
            : data.voter_targeting.top_archetypes,
            
        // Messaging (Ward Only)
        messaging: isWardMode ? data.voter_targeting?.messaging_priorities : null
    };
    
    const winnerColorArray = PARTY_COLORS[displayProps.lastElectionWinner] || PARTY_COLORS.DEFAULT;

    // --- INTELLIGENT COLOR MAPPING FOR ARCHETYPES ---
    const validParties = useMemo(() => {
        if (isWardMode) return null; 
        const votes = displayProps.voteData;
        return Object.entries(votes)
            .sort((a,b) => b[1] - a[1])
            .slice(0, 3) // Top 3 Parties
            .map(entry => entry[0]);
    }, [isWardMode, displayProps.voteData]);

    // Pre-compute total votes for progress bars
    const totalVotes = Object.values(displayProps.voteData).reduce((s, v) => s + v, 0);

    // Glass card style — consistent with the outer panel material
    const glassCard = {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px',
        padding: '16px',
        marginBottom: '12px',
    };

    const sectionLabel = "text-xs font-bold uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2";

    return (
        <div className="overflow-y-auto h-full" style={{scrollbarWidth:'thin', scrollbarColor:'rgba(255,255,255,0.1) transparent'}}>
            <div className="p-5 space-y-3">

                {/* ── Panel Header ── */}
                <div className="flex justify-between items-center mb-1">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-0.5">
                            {isWardMode ? 'Ward Intelligence' : 'Campaign Intelligence'}
                        </p>
                        <h2 className="text-xl font-bold text-white leading-tight">{displayProps.title}</h2>
                    </div>
                    {isWardMode ? (
                        <button onClick={onClose}
                            className="shrink-0 ml-3 p-2 rounded-xl text-gray-500 hover:text-white hover:bg-white/10 transition-all"
                            title="Close Ward View">
                            <FiX size={18}/>
                        </button>
                    ) : (
                        <div className="p-2 rounded-xl bg-white/5 text-gray-500">
                            <FiMap size={18}/>
                        </div>
                    )}
                </div>

                {/* ── Strategic Overview ── */}
                <div style={glassCard}>
                    <p className={sectionLabel}><FiShield size={11}/> Strategic Overview</p>

                    {/* Status + Strategy side by side */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="rounded-xl p-3" style={{background:'rgba(255,255,255,0.04)'}}>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">Status</p>
                            <Pill text={displayProps.statusTier} tier={displayProps.statusTier}/>
                        </div>
                        <div className="rounded-xl p-3" style={{background:'rgba(255,255,255,0.04)'}}>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">Strategy</p>
                            <p className="text-xs text-purple-300 font-mono font-semibold leading-tight">{displayProps.strategy?.replace(/_/g, ' ')}</p>
                        </div>
                    </div>

                    {/* Primary Objective */}
                    {displayProps.primaryObjective && (
                        <div className="rounded-xl p-3" style={{background:'rgba(6,182,212,0.06)', border:'1px solid rgba(6,182,212,0.15)'}}>
                            <p className="text-xs text-cyan-500 uppercase tracking-wide mb-1 font-bold">Primary Objective</p>
                            <p className="text-sm text-white leading-snug">{displayProps.primaryObjective}</p>
                        </div>
                    )}

                    {/* Resource Allocation (campaign mode) */}
                    {displayProps.resourceCounts && (
                        <div className="mt-3 pt-3" style={{borderTop:'1px solid rgba(255,255,255,0.06)'}}>
                            <p className="text-xs text-gray-500 uppercase tracking-wide font-bold mb-2 flex items-center gap-1.5">
                                <FiPieChart size={11}/> Resource Allocation
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(displayProps.resourceCounts).map(([key, count]) => {
                                    const accent = key === 'MAXIMUM' ? 'rgba(239,68,68,0.15)' : key === 'HIGH' ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.04)';
                                    const textCol = key === 'MAXIMUM' ? 'text-red-400' : key === 'HIGH' ? 'text-orange-400' : 'text-gray-300';
                                    return (
                                        <div key={key} className="rounded-xl px-3 py-2 flex justify-between items-center"
                                            style={{background: accent}}>
                                            <span className="text-xs text-gray-400 uppercase tracking-wide">{key}</span>
                                            <span className={`text-base font-bold ${textCol}`}>{count}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Win Path Analysis ── */}
                <div style={glassCard}>
                    <p className={sectionLabel}><FiTrendingUp size={11}/> Win Path Analysis</p>

                    {/* Winability — hero number */}
                    <div className="rounded-xl p-4 mb-3 text-center" style={{background:'rgba(6,182,212,0.07)', border:'1px solid rgba(6,182,212,0.15)'}}>
                        <p className="text-4xl font-black text-white tracking-tight leading-none">{displayProps.winabilityScore}%</p>
                        <p className="text-xs text-cyan-500 uppercase tracking-widest mt-1 font-bold">{isWardMode ? 'Winability' : 'Avg. Winability'}</p>
                    </div>

                    {/* Margin + Swing side by side */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="rounded-xl p-3 text-center" style={{background:'rgba(255,255,255,0.04)'}}>
                            <p className={`text-lg font-bold leading-none ${displayProps.marginVotes > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {displayProps.marginVotes?.toLocaleString() || 0}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">{isWardMode ? 'Current Margin' : 'Aggregate Margin'}</p>
                        </div>
                        <div className="rounded-xl p-3 text-center" style={{background:'rgba(255,255,255,0.04)'}}>
                            <p className="text-lg font-bold text-white leading-none">{(displayProps.swingRequired || 0).toFixed(1)}%</p>
                            <p className="text-xs text-gray-500 mt-1">{isWardMode ? 'Swing Required' : 'Target Swing'}</p>
                        </div>
                    </div>

                    {/* Voter pools */}
                    <div className="space-y-2">
                        {[
                            {label:'Persuadable Pool', value: displayProps.persuadablePool, color:'rgba(6,182,212,0.7)'},
                            {label:'Squeeze Pool',     value: displayProps.squeezePool,     color:'rgba(168,85,247,0.7)'},
                        ].map(({label, value, color}) => (
                            <div key={label}>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs text-gray-400">{label}</span>
                                    <span className="text-xs font-bold text-white">{(value || 0).toLocaleString()}</span>
                                </div>
                                <div className="h-1 rounded-full w-full" style={{background:'rgba(255,255,255,0.06)'}}>
                                    <div className="h-1 rounded-full" style={{width:`${Math.min(100, ((value||0) / Math.max(displayProps.persuadablePool||1, displayProps.squeezePool||1)) * 100)}%`, background:color}}/>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Opportunities + Risks */}
                    {(displayProps.opportunities?.length > 0 || displayProps.risks?.length > 0) && (
                        <div className="mt-3 grid grid-cols-2 gap-2">
                            {displayProps.opportunities?.length > 0 && (
                                <div className="rounded-xl p-3" style={{background:'rgba(34,197,94,0.07)', border:'1px solid rgba(34,197,94,0.15)'}}>
                                    <p className="text-xs font-bold text-green-400 mb-2 flex items-center gap-1"><FiCheckCircle size={10}/>Opportunities</p>
                                    <ul className="space-y-1">
                                        {displayProps.opportunities.map((f,i) => (
                                            <li key={i} className="text-xs text-gray-300 leading-snug before:content-['·'] before:mr-1 before:text-green-500">{f}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {displayProps.risks?.length > 0 && (
                                <div className="rounded-xl p-3" style={{background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.15)'}}>
                                    <p className="text-xs font-bold text-red-400 mb-2 flex items-center gap-1"><FiXCircle size={10}/>Risks</p>
                                    <ul className="space-y-1">
                                        {displayProps.risks.map((f,i) => (
                                            <li key={i} className="text-xs text-gray-300 leading-snug before:content-['·'] before:mr-1 before:text-red-500">{f}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Last Election / Popular Vote ── */}
                <div style={glassCard}>
                    <p className={sectionLabel}><FiBarChart2 size={11}/> {isWardMode ? 'Last Election' : 'Campaign Popular Vote'}</p>

                    {/* Winner banner */}
                    <div className="rounded-xl px-4 py-2.5 mb-3 flex items-center justify-between"
                        style={{background: getCssColor(winnerColorArray, 0.12), border:`1px solid ${getCssColor(winnerColorArray, 0.35)}`}}>
                        <span className="text-xs text-gray-400 uppercase tracking-wide font-bold">{isWardMode ? 'Winner' : 'Overall Winner'}</span>
                        <span className="font-black text-sm" style={{color: getCssColor(winnerColorArray, 1)}}>{displayProps.lastElectionWinner}</span>
                    </div>

                    {/* Vote bars */}
                    <div className="space-y-2.5">
                        {Object.entries(displayProps.voteData)
                            .sort((a,b) => b[1]-a[1])
                            .map(([party, votes]) => {
                                const col = PARTY_COLORS[party] || PARTY_COLORS.DEFAULT;
                                const pct = totalVotes > 0 ? (votes / totalVotes * 100) : 0;
                                return (
                                    <div key={party}>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-xs font-semibold" style={{color: getCssColor(col,1)}}>{party}</span>
                                            <span className="text-xs text-gray-400">{votes.toLocaleString()} <span className="text-gray-600">({pct.toFixed(1)}%)</span></span>
                                        </div>
                                        <div className="h-1.5 rounded-full w-full" style={{background:'rgba(255,255,255,0.06)'}}>
                                            <div className="h-1.5 rounded-full transition-all" style={{width:`${pct}%`, background: getCssColor(col, 0.85)}}/>
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>

                {/* ── Priority Voter Groups / Dominant Archetypes ── */}
                <div style={glassCard}>
                    <p className={sectionLabel}><FiUsers size={11}/> {isWardMode ? 'Priority Voter Groups' : 'Dominant Archetypes'}</p>
                    <div className="space-y-2">
                        {displayProps.archetypes
                            .sort((a,b) => {
                                const cA = a.population_metrics?.estimated_population || a.count || 0;
                                const cB = b.population_metrics?.estimated_population || b.count || 0;
                                return cB - cA;
                            })
                            .slice(0,5)
                            .map((arch) => {
                                const meta = ARCHETYPE_DETAILS[arch.name.toUpperCase().replace(/ /g,'_').replace(/-/g,'_')] || {};
                                let party = meta.party || 'Other';
                                let partyColor = PARTY_COLORS[party] || PARTY_COLORS.DEFAULT;
                                if (!isWardMode && validParties && !validParties.includes(party)) {
                                    partyColor = PARTY_COLORS.Other;
                                }
                                const persuadePct = ((arch.campaign_targeting?.persuadability_score || 0) * 100).toFixed(0);
                                const pop = (arch.population_metrics?.estimated_population || arch.count || 0).toLocaleString();
                                return (
                                    <div key={arch.name} className="rounded-2xl p-3 flex items-center gap-3"
                                        style={{background:`rgba(${partyColor[0]},${partyColor[1]},${partyColor[2]},0.08)`, borderLeft:`3px solid rgba(${partyColor[0]},${partyColor[1]},${partyColor[2]},0.7)`}}>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-white leading-tight truncate">{arch.name}</p>
                                            <p className="text-xs text-gray-400 mt-0.5">Pop: <span className="text-gray-300 font-medium">{pop}</span></p>
                                        </div>
                                        {isWardMode && (
                                            <div className="shrink-0 text-right">
                                                <p className="text-base font-black text-white">{persuadePct}%</p>
                                                <p className="text-xs text-gray-500 leading-none">persuadable</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>

                {/* ── Core Messaging (ward only) ── */}
                {displayProps.messaging && (
                    <div style={glassCard}>
                        <p className={sectionLabel}><FiMessageSquare size={11}/> Core Messaging</p>

                        {/* Moral axis */}
                        {displayProps.messaging.deep_messaging_frame?.primary_moral_axis && (
                            <div className="rounded-xl px-3 py-2.5 mb-3" style={{background:'rgba(168,85,247,0.08)', border:'1px solid rgba(168,85,247,0.2)'}}>
                                <p className="text-xs text-purple-400 uppercase tracking-wide font-bold mb-0.5">Primary Moral Axis</p>
                                <p className="text-sm text-purple-300 font-mono">{displayProps.messaging.deep_messaging_frame.primary_moral_axis}</p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-2">
                            {displayProps.messaging.what_resonates?.length > 0 && (
                                <div className="rounded-xl p-3" style={{background:'rgba(34,197,94,0.07)', border:'1px solid rgba(34,197,94,0.15)'}}>
                                    <p className="text-xs font-bold text-green-400 mb-1.5">What Resonates</p>
                                    <p className="text-xs text-gray-300 italic leading-snug">"{displayProps.messaging.what_resonates[0]}"</p>
                                </div>
                            )}
                            {displayProps.messaging.what_to_avoid?.length > 0 && (
                                <div className="rounded-xl p-3" style={{background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.15)'}}>
                                    <p className="text-xs font-bold text-red-400 mb-1.5">What to Avoid</p>
                                    <p className="text-xs text-gray-300 italic leading-snug">"{displayProps.messaging.what_to_avoid[0]}"</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default function CampaignMap({campaign, onMobileChange}) {
    const [selectedWard, setSelectedWard] = useState(null);
    const [mapView, setMapView] = useState('swing');
    const [historicElectionType, setHistoricElectionType] = useState('GE24'); // New State Added
    const [processedData, setProcessedData] = useState(null);
    const [summaryStats, setSummaryStats] = useState(null);
    const [campaignOverview, setCampaignOverview] = useState(null); 
    const [authorityName, setAuthorityName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [features, setFeatures] = useState([]);
    const [initialViewState, setInitialViewState] = useState(DEFAULT_VIEW_STATE);
    
    // --- Filter State ---
    const [searchTerm, setSearchTerm] = useState('');
    const [tierFilter, setTierFilter] = useState('ALL');
    const [archetypeFilter, setArchetypeFilter] = useState('ALL'); 
    const [availableTiers, setAvailableTiers] = useState([]);
    const [availableArchetypes, setAvailableArchetypes] = useState([]); 

    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [modalState, setModalState] = useState({ isOpen: false, data: null, wardName: '' });
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isPanelExpanded, setIsPanelExpanded] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(108);
    const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1400);
    const [vh, setVh] = useState(typeof window !== 'undefined' ? window.innerHeight : 900);
    const lastClickRef = useRef(0);

    // Track real viewport dimensions on every resize/rotation
    useEffect(() => {
        const sync = () => { setVw(window.innerWidth); setVh(window.innerHeight); };
        sync();
        window.addEventListener('resize', sync);
        window.addEventListener('orientationchange', () => setTimeout(sync, 150));
        return () => window.removeEventListener('resize', sync);
    }, []);

    // Mobile = portrait phone (narrow) OR landscape phone (short + not a tablet/desktop)
    const useMobileUI = vw < 640 || (vh < 500 && vw < 1024);
    // Medium = tablet / small desktop — two-row header, narrower sidebar
    const isMedium = !useMobileUI && vw < 1380;

    // Tell the parent shell to collapse its sidebar when we go mobile
    useEffect(() => {
        if (onMobileChange) onMobileChange(useMobileUI);
    }, [useMobileUI]);

    const hasActiveFilters = searchTerm !== '' || tierFilter !== 'ALL' || archetypeFilter !== 'ALL';

    const clearFilters = () => {
        setSearchTerm('');
        setTierFilter('ALL');
        setArchetypeFilter('ALL');
    };

    useMemo(() => fetch(`https://us-central1-knoesis-dante.cloudfunctions.net/getCampaignGeoJSON?campaignID=${campaign?.campaignID}`).then(res => res.json()).then(json => setFeatures(json)),[campaign]);

    useEffect(() => {
        try {
            console.hidden('Processing data...');
            const REGION = Object.keys(campaign.wards)[0];
            const AUTHORITY = Object.keys(campaign.wards[REGION])[0];
            const authorityWards = Object.values(campaign.wards[REGION][AUTHORITY]);

            // --- AGGREGATION VARIABLES ---
            let gainCount = 0;
            let defendCount = 0;
            let winabilitySum = 0;
            let totalWards = 0;
            
            // Stats for Sidebar Overview
            const totalHistoricVotes = {};
            const archetypeCounts = {};
            const tierCounts = {};      
            const resourceCounts = {};  
            
            // New "Campaign Brain" Metrics
            let aggregateMarginVotes = 0;
            let totalPersuadable = 0;
            let totalSqueeze = 0;
            let totalSwingRequired = 0;
            let targetWardsCount = 0;
            
            // Fallbacks if we need to calculate
            let highestGainTier = 'DEFAULT';
            let highestDefendTier = 'DEFAULT';
            let highestGainIndex = 999;
            let highestDefendIndex = 999;

            const wardLookup = {};
            const tiersSet = new Set(); 

            Object.values(authorityWards).forEach(ward => {
                const key = ward.ward_name.toLowerCase().replace(/ & /g, ' and ').replace(/[.']/g, '').trim();
                wardLookup[key] = ward;
                
                const tier = ward.strategic_priority?.tier || ward.competitive_tier;
                if(tier) {
                    tiersSet.add(tier);
                    // Add to count map
                    tierCounts[tier] = (tierCounts[tier] || 0) + 1;
                    
                    // Simple gain/defend split for stats
                    if (tier.includes('GAIN')) {
                        gainCount++;
                        const pIndex = TIER_PRECEDENCE.indexOf(tier);
                        if (pIndex !== -1 && pIndex < highestGainIndex) {
                            highestGainIndex = pIndex;
                            highestGainTier = tier;
                        }
                    } else if (tier.includes('DEFEND') || tier.includes('HOLD')) {
                        defendCount++;
                        const pIndex = TIER_PRECEDENCE.indexOf(tier);
                        if (pIndex !== -1 && pIndex < highestDefendIndex) {
                            highestDefendIndex = pIndex;
                            highestDefendTier = tier;
                        }
                    }
                }

                // Count Resource Allocation
                const res = ward.strategic_priority?.resource_allocation;
                if (res) {
                    resourceCounts[res] = (resourceCounts[res] || 0) + 1;
                }

                const score = ward.winability?.score || ward.winability_score || 0;
                winabilitySum += score;
                totalWards++;

                // --- AGGREGATE METRICS FOR CAMPAIGN OVERVIEW ---
                aggregateMarginVotes += (ward.swing_analysis?.margin_votes || 0);
                totalPersuadable += (ward.voter_targeting?.persuadable_voters || 0);
                totalSqueeze += (ward.voter_targeting?.squeeze_opportunities?.Squeeze_Total_Pool_BQ || 0);
                
                if (tier?.includes('GAIN') || tier?.includes('DEFEND_CRITICAL')) {
                    totalSwingRequired += (ward.swing_analysis?.swing_required_pct || 0);
                    targetWardsCount++;
                }

                // Historic Aggregation
                const historic = ward.last_election_results?.party_votes || {};
                Object.entries(historic).forEach(([party, count]) => {
                    totalHistoricVotes[party] = (totalHistoricVotes[party] || 0) + count;
                });

                // Archetype Aggregation
                const wardArchetypes = ward.voter_targeting?.priority_archetypes || [];
                const archList = Array.isArray(wardArchetypes) ? wardArchetypes : Object.values(wardArchetypes);
                archList.forEach(arch => {
                     const name = arch.name;
                     const pop = arch.population_metrics?.estimated_population || 0;
                     if(name) {
                         archetypeCounts[name] = (archetypeCounts[name] || 0) + pop;
                     }
                });
            });

            // --- SYNTHESIZE CAMPAIGN STRATEGY (UPDATED V2) ---
            let campaignTier = 'NO_CONTEST';
            
            // 1. PRIMARY: Check if the summary export exists and use it
            const exportedTierCounts = campaign.swing_analysis?.campaign_summary?.strategic_tier_counts;
            
            // Use either the exported counts or the ones we manually aggregated
            const countsToUse = exportedTierCounts || tierCounts;

            // Sort by count descending
            if (countsToUse) {
                const sortedTiers = Object.entries(countsToUse).sort((a, b) => b[1] - a[1]);
                if (sortedTiers.length > 0) {
                    // The highest count wins
                    campaignTier = sortedTiers[0][0];
                }
            } else {
                // Fallback (unlikely)
                campaignTier = (gainCount > defendCount) ? highestGainTier : highestDefendTier;
            }
            
            let campaignStrategy = 'Maintain current holdings while monitoring opportunities.';
            let primaryObjective = `Defend ${defendCount} seats.`;

            if (campaignTier.includes('GAIN')) {
                campaignStrategy = 'Aggressive expansion into target territories.';
                primaryObjective = `Gain ${gainCount} target seats while holding base.`;
            } else if (campaignTier.includes('DEFEND')) {
                 campaignStrategy = 'Active defense of marginals with targeted gains.';
                 primaryObjective = `Protect ${defendCount} critical seats and convert top gains.`;
            }

            // --- FINALIZE AGGREGATE STATS ---
            const avgWinability = totalWards > 0 ? winabilitySum / totalWards : 0;
            const avgSwingRequired = targetWardsCount > 0 ? totalSwingRequired / targetWardsCount : 0;
            
            let historicWinner = 'None';
            let maxVotes = 0;
            Object.entries(totalHistoricVotes).forEach(([party, votes]) => {
                if(votes > maxVotes) {
                    maxVotes = votes;
                    historicWinner = party;
                }
            });

            const sortedArchetypes = Object.entries(archetypeCounts)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count);
                
            // Check for exported resource counts, else use calculated
            const finalResourceCounts = campaign.swing_analysis?.campaign_summary?.resource_allocation_counts || resourceCounts;
            const highPriorityResources = (finalResourceCounts['MAXIMUM'] || 0) + (finalResourceCounts['HIGH'] || 0);

            const computedSummary = {
                avgWinability,
                gainTargets: gainCount,
                defendTargets: defendCount,
                campaignTier, // Now based on highest frequency
                highPriorityResources // For Header Stats
            };
            
            setCampaignOverview({
                ward_name: "Campaign Overview",
                strategic_priority: {
                    tier: campaignTier,
                    campaign_strategy: campaignStrategy,
                    primary_objective: primaryObjective
                },
                resource_allocation_counts: finalResourceCounts, // Passed to Sidebar
                winability: {
                    avg_score: avgWinability,
                    aggregate_margin_votes: aggregateMarginVotes,
                    avg_swing_required: avgSwingRequired,
                    opportunities: [
                        `Large aggregate persuadable pool of ${totalPersuadable.toLocaleString()} voters.`,
                        gainCount > 0 ? `${gainCount} clear gain targets identified.` : "Strong defensive position established.",
                        totalSqueeze > 0 ? `Potential to squeeze ${totalSqueeze.toLocaleString()} 3rd party voters.` : null
                    ].filter(Boolean),
                    risks: [
                         aggregateMarginVotes < 0 ? "Overall popular vote deficit across target area." : null,
                         defendCount > 5 ? "High volume of defensive seats strains resources." : null
                    ].filter(Boolean)
                },
                voter_targeting: {
                    total_persuadable: totalPersuadable,
                    total_squeeze: totalSqueeze,
                    top_archetypes: sortedArchetypes
                },
                historic_voting: {
                    overall_winner: historicWinner,
                    total_votes: totalHistoricVotes
                }
            });

            setAvailableTiers(Array.from(tiersSet).sort());

            const processed = features.map(feature => {
                const geoName = feature.properties.ward_name;
                const key = geoName.toLowerCase().replace(/ & /g, ' and ').replace(/[.']/g, '').trim();
                const wardDetails = wardLookup[key];
                
                if (wardDetails) {
                    const archetypes = Object.values(wardDetails.voter_archetypes || wardDetails.voter_targeting?.priority_archetypes || {});
                    const dominant = archetypes.sort((a, b) => (b.count || b.population_metrics?.estimated_population || 0) - (a.count || a.population_metrics?.estimated_population || 0))[0];

                    console.hidden(wardDetails);
                    
                    // NEW: Extract GE24 and Local Election Winners
                    const lastElection = wardDetails.historical_voting?.GE24;
                    const geWinner = lastElection?.winner;
                    const localWinner = wardDetails.historical_voting?.last_local_election?.winner;
                    
                    let dominantKey = 'DEFAULT';
                    let dominantName = 'Unknown';

                    if (dominant) {
                        dominantName = dominant.name || 'Unknown';
                        dominantKey = dominantName.toUpperCase().replace(/ /g, '_').replace(/-/g, '_');
                    }

                    return {
                        type: 'Feature',
                        geometry: JSON.parse(feature.geometry),
                        properties: {
                            ...feature.properties,
                            ...wardDetails,
                            dominant_archetype_key: dominantKey, 
                            dominant_archetype_name: dominantName,
                            last_election_winner: lastElection.winner, // Left untouched for InfoPanel
                            ge_election_winner: geWinner,              // Extracted for map coloring
                            local_election_winner: localWinner,        // Extracted for map coloring
                            isTargetWard: true,
                            competitive_tier: wardDetails.strategic_priority?.tier || wardDetails.competitive_tier || 'DEFAULT'
                        }
                    };
                }
            });

            const finalFeatures = processed.filter(Boolean);
            const geoJsonData = {
                type: 'FeatureCollection',
                features: finalFeatures
            };
            
            // --- Extract Available Archetypes from Features ---
            const archSet = new Set();
            finalFeatures.forEach(f => {
                if (f.properties.dominant_archetype_name && f.properties.dominant_archetype_name !== 'Unknown') {
                    archSet.add(f.properties.dominant_archetype_name);
                }
            });
            setAvailableArchetypes(Array.from(archSet).sort());

            if (finalFeatures.length > 0) {
                const autoView = calculateBounds(finalFeatures);
                if (autoView) setInitialViewState(autoView);
            }

            setProcessedData(geoJsonData);
            setSummaryStats(computedSummary);
            setAuthorityName(AUTHORITY);
            setLoading(false);
        } catch (err) {
            console.error('Error processing data:', err);
            setError(err.message);
            setLoading(false);
        }
    }, [campaign.wards, features]);

    // --- Filter Logic Helper ---
    const isFeatureVisible = (props) => {
        if (!props.isTargetWard) return false;

        if (searchTerm && !props.ward_name.toLowerCase().includes(searchTerm.toLowerCase())) {
            return false;
        }
        if (tierFilter !== 'ALL' && props.competitive_tier !== tierFilter) {
            return false;
        }
        if (archetypeFilter !== 'ALL' && props.dominant_archetype_name !== archetypeFilter) {
            return false;
        }

        return true;
    };

    const getFillColor = (d) => {
        const props = d.properties;
        
        if (!isFeatureVisible(props)) return [40, 40, 45, 50]; 
        
        if (mapView === 'swing') {
            const tier = props.competitive_tier;
            return TIER_COLORS[tier] || TIER_COLORS.DEFAULT;
        }
        
        if (mapView === 'archetype') {
            const meta = ARCHETYPE_DETAILS[props.dominant_archetype_key];
            const party = meta ? meta.party : 'DEFAULT';
            return PARTY_COLORS[party] || PARTY_COLORS.DEFAULT;
        }
        
        if (mapView === 'historic') {
            // Updated to respect the drop-down toggle
            let winner;
            if (historicElectionType === 'Local') {
                winner = props.local_election_winner;
            } else {
                winner = props.ge_election_winner || props.last_election_winner;
            }
            return PARTY_COLORS[winner] || PARTY_COLORS.DEFAULT;
        }
        return TIER_COLORS.DEFAULT;
    };

    const getLineColor = (d) => {
        const props = d.properties;
        if (!isFeatureVisible(props)) return [80, 80, 85, 50]; 
        
        const isSelected = selectedWard?.properties?.ward_id === props.ward_id;
        return isSelected ? [0, 255, 255, 255] : [255, 255, 255, 255];
    };

    const handleLayerClick = (info) => {
        if (!info.object?.properties.isTargetWard) return;

        const now = Date.now();
        const timeDiff = now - lastClickRef.current;
        const isDoubleClick = timeDiff < 300; 
        lastClickRef.current = now;

        setSelectedWard(info.object);
        if (useMobileUI || isMedium) setIsPanelOpen(true);

        if (isDoubleClick && mapView === 'archetype') {
            const props = info.object.properties;
            const details = ARCHETYPE_DETAILS[props.dominant_archetype_key];
            if (details) {
                setModalState({
                    isOpen: true,
                    data: { ...details, name: props.dominant_archetype_name },
                    wardName: props.ward_name
                });
            }
        }
    };

    const layers = [
        new GeoJsonLayer({
            id: 'wards-geojson',
            data: processedData,
            pickable: true,
            stroked: true,
            filled: true,
            extruded: false,
            lineWidthScale: 1,
            lineWidthMinPixels: 2,
            getFillColor: getFillColor,
            getLineColor: getLineColor, 
            getLineWidth: 1,
            onClick: handleLayerClick,
            updateTriggers: {
                // Added historicElectionType state to trigger re-renders
                getFillColor: [mapView, searchTerm, tierFilter, archetypeFilter, historicElectionType], 
                getLineColor: [selectedWard, searchTerm, tierFilter, archetypeFilter],
            },
        }),
    ];

    if (loading) return <div className="w-full h-screen bg-gray-900 flex items-center justify-center"><div className="text-white text-xl">Loading map data...</div></div>;
    if (error) return <div className="w-full h-screen bg-gray-900 flex items-center justify-center"><div className="text-red-400 text-xl">Error: {error}</div></div>;

    // ── Derived display values used by the mobile header pill ──
    const TIER_STYLES_MAP = {
        GAIN_PRIORITY:'bg-yellow-500 text-yellow-900',GAIN_TARGET:'bg-yellow-400 text-yellow-900',
        DEFEND_CRITICAL:'bg-red-600 text-white',DEFEND_URGENT:'bg-orange-500 text-white',
        DEFEND_PRIORITY:'bg-sky-500 text-white',LONG_TERM_TARGET:'bg-blue-400 text-white',
        SAFE_HOLD:'bg-green-500 text-white',UNWINNABLE:'bg-slate-600 text-white',
        WATCH_LIST:'bg-purple-500 text-white',NO_CONTEST:'bg-purple-500 text-white',
        DEFAULT:'bg-gray-700 text-white'
    };
    const mobileTier  = selectedWard?.properties
        ? (selectedWard.properties.strategic_priority?.tier || selectedWard.properties.competitive_tier)
        : summaryStats?.campaignTier;
    const mobileTierClass = TIER_STYLES_MAP[mobileTier] || TIER_STYLES_MAP.DEFAULT;
    const mobileTitle = selectedWard?.properties?.ward_name
        || (authorityName ? authorityName + ' Overview' : 'Overview');

    return (
        <div className="w-full h-[90vh] bg-gray-900 relative overflow-hidden">
            {/* ── Tone down MapLibre attribution (legally must stay) ── */}
            <style>{`
                .maplibregl-ctrl-attrib{font-size:9px!important;opacity:.3!important;
                  background:rgba(0,0,0,.25)!important;padding:1px 4px!important;
                  border-radius:3px!important;max-width:155px!important}
                .maplibregl-ctrl-attrib a{color:#777!important}
                .maplibregl-ctrl-attrib-button{display:none!important}
            `}</style>

            {/* ── DESKTOP floating glass header — overlays the map ── */}
            {!useMobileUI && (
                <HeaderStats selectedWardData={selectedWard?.properties} summaryStats={summaryStats} authorityName={authorityName} vw={vw} onHeightChange={isMedium ? setHeaderHeight : undefined} />
            )}

            <div className="absolute inset-0 flex">
                <div className="flex-grow relative">
                    <DeckGL
                        initialViewState={initialViewState}
                        controller={{ doubleClickZoom: false }} 
                        layers={layers}
                        getTooltip={({object}) => {
                            if (!object?.properties) return null;
                            const props = object.properties;
                            
                            if (!isFeatureVisible(props)) return { html: `<div style="background: #1a202c; color: #94a3b8; padding: 8px; border-radius: 4px; font-size: 12px;">${props.ward_name}</div>` };

                            const name = props.ward_name;
                            
                            if (props.isTargetWard) {
                                let content = `<strong style="font-size: 14px; display:block; margin-bottom:6px;">${name}</strong>`;
                                
                                switch (mapView) {
                                    case 'swing':
                                        const tier = (props.strategic_priority?.tier || props.competitive_tier)?.replace(/_/g, ' ') || 'N/A';
                                        const winability = (((props.winability?.score || props.winability_score) || 0) * 100).toFixed(1);
                                        content += `<div style="font-size: 12px; color: #38bdf8;">Tier: ${tier}</div>`;
                                        content += `<div style="font-size: 11px; color: #e2e8f0;">Winability: ${winability}%</div>`;
                                        break;

                                    case 'archetype':
                                        const archKey = props.dominant_archetype_key;
                                        const archName = props.dominant_archetype_name;
                                        const meta = ARCHETYPE_DETAILS[archKey] || {};
                                        const party = meta.party || 'Unknown';
                                        const neighbors = meta.neighbors || {};

                                        content += `<div style="border-bottom: 1px solid #475569; padding-bottom: 4px; mb-2">`;
                                        content += `<div style="font-size: 12px; color: #38bdf8; font-weight:bold;">${archName}</div>`;
                                        content += `<div style="font-size: 11px; color: #94a3b8;">Leaning: <span style="color:white;">${party}</span></div>`;
                                        content += `</div>`;
                                        content += `<div style="font-size: 10px; color: #cbd5e1; margin-bottom:6px;">Double-click for Bio</div>`;

                                        if (neighbors.prog || neighbors.cons) {
                                            content += `<div style="margin-top:6px;">`;
                                            content += `<div style="font-size: 10px; text-transform:uppercase; color: #64748b; margin-bottom:2px;">Neighbours</div>`;
                                            if (neighbors.prog) content += `<div style="font-size: 10px; color: #ef4444;">← ${neighbors.prog.replace(/_/g, ' ')}</div>`;
                                            if (neighbors.cons) content += `<div style="font-size: 10px; color: #3b82f6;">→ ${neighbors.cons.replace(/_/g, ' ')}</div>`;
                                            content += `</div>`;
                                        }
                                        break;

                                    case 'historic':
                                        // Updated Tooltip for Historic Views
                                        const isLocal = historicElectionType === 'Local';
                                        const winner = isLocal 
                                            ? props.local_election_winner 
                                            : (props.ge_election_winner || props.last_election_winner || 'N/A');
                                        const electionLabel = isLocal ? 'Last Local Election' : 'General Election';
                                        
                                        content += `<div style="font-size: 12px; color: #38bdf8;">${electionLabel} Winner: ${winner || 'N/A'}</div>`;
                                        break;
                                    default: break;
                                }
                                return {
                                    html: `<div style="background: #1a202c; color: white; padding: 12px; border-radius: 6px; font-family: sans-serif; box-shadow: 0 4px 12px rgba(0,0,0,0.5); min-width: 150px;">${content}</div>`,
                                    style: { backgroundColor: 'transparent', pointerEvents: 'none' }
                                };
                            } 
                            return null;
                        }}
                    >
                        <MapGL mapStyle={MAP_STYLE} />
                    </DeckGL>

                    {/* ── MOBILE top bar: view pills + filter trigger in one row ── */}
                    {useMobileUI && (
                        <div className="absolute top-3 left-3 right-3 z-10 flex items-center gap-1.5">
                            {/* View pills — icon+label in landscape, icon-only in portrait to preserve space */}
                            {[
                                {view:'swing',    icon:<FiTrendingUp size={13}/>,  label:'Wards'},
                                {view:'archetype',icon:<FiUsers size={13}/>,       label:'Voters'},
                                {view:'historic', icon:<FiBarChart2 size={13}/>,   label:'Historic'},
                            ].map(({view,icon,label}) => (
                                <button key={view} onClick={() => setMapView(view)} title={label}
                                    className={`flex items-center gap-1 rounded-full shadow-lg font-semibold backdrop-blur-sm border transition-all shrink-0 ${mapView === view ? 'bg-cyan-500 border-cyan-400 text-white' : 'bg-gray-900/85 border-gray-700 text-gray-300'}`}
                                    style={{fontSize:'11px', padding: vw < 640 ? '7px 9px' : '5px 10px'}}
                                >
                                    {icon}
                                    {/* Show label only in landscape (wider) — portrait is icon-only */}
                                    {vw >= 640 && <span>{label}</span>}
                                </button>
                            ))}

                            {/* Spacer */}
                            <div className="flex-1"/>

                            {/* Filter trigger — always shows icon + label */}
                            <button
                                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                                className={`flex items-center gap-1 rounded-full shadow-lg font-semibold backdrop-blur-sm border transition-all shrink-0 ${hasActiveFilters ? 'bg-cyan-500 border-cyan-400 text-white' : isFiltersOpen ? 'bg-gray-700 border-gray-500 text-white' : 'bg-gray-900/85 border-gray-700 text-gray-300'}`}
                                style={{fontSize:'11px', padding:'5px 10px'}}
                            >
                                <FiFilter size={13}/>
                                <span>Filters</span>
                                {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-white ml-0.5"/>}
                            </button>

                            {/* Filter dropdown — anchored below the row, right-aligned */}
                            {isFiltersOpen && (
                                <div className="absolute top-10 right-0 z-20 w-56 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                                    style={{background:'rgba(8,12,24,0.92)', backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)'}}>
                                    <div className="p-3 flex flex-col gap-3">
                                        {hasActiveFilters && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                                                className="text-xs text-red-400 flex items-center gap-1 self-end"
                                            >
                                                <FiTrash2 size={11}/> Clear all
                                            </button>
                                        )}
                                        <div>
                                            <label className="text-xs text-gray-500 mb-1 block uppercase font-semibold tracking-wide">Search</label>
                                            <div className="relative">
                                                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={12}/>
                                                <input type="text" placeholder="Find a ward…" value={searchTerm}
                                                    onChange={e => setSearchTerm(e.target.value)}
                                                    className="w-full rounded-lg px-2.5 pl-8 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/20"  style={{background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)'}}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500 mb-1 block uppercase font-semibold tracking-wide">Priority Tier</label>
                                            <div className="relative">
                                                <select value={tierFilter} onChange={e => setTierFilter(e.target.value)}
                                                    className="w-full rounded-lg px-2.5 py-1.5 text-xs text-white appearance-none focus:outline-none focus:border-cyan-500/60" style={{background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)'}}>
                                                    <option value="ALL">All Priorities</option>
                                                    {availableTiers.map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
                                                </select>
                                                <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={12}/>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500 mb-1 block uppercase font-semibold tracking-wide">Voter Archetype</label>
                                            <div className="relative">
                                                <select value={archetypeFilter} onChange={e => setArchetypeFilter(e.target.value)}
                                                    className="w-full rounded-lg px-2.5 py-1.5 text-xs text-white appearance-none focus:outline-none focus:border-cyan-500/60" style={{background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)'}}>
                                                    <option value="ALL">All Archetypes</option>
                                                    {availableArchetypes.map(a => <option key={a} value={a}>{a}</option>)}
                                                </select>
                                                <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={12}/>
                                            </div>
                                        </div>
                                        {mapView === 'historic' && (
                                            <div>
                                                <label className="text-xs text-gray-500 mb-1 block uppercase font-semibold tracking-wide">Historic Election</label>
                                                <div className="relative">
                                                    <select value={historicElectionType} onChange={e => setHistoricElectionType(e.target.value)}
                                                        className="w-full rounded-lg px-2.5 py-1.5 text-xs text-white appearance-none focus:outline-none focus:border-cyan-500/60" style={{background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)'}}>
                                                        <option value="GE24">General Election</option>
                                                        <option value="Local">Last Local Election</option>
                                                    </select>
                                                    <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={12}/>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── DESKTOP: glassmorphic controls, pushed below the header ── */}
                    {!useMobileUI && (
                        <>
                            <div className={`absolute ${isMedium ? '' : 'top-20'} left-4 backdrop-blur-md p-2 rounded-2xl border border-white/10 flex gap-2 z-10 shadow-xl items-center`}
                                style={{background:'rgba(10,15,28,0.72)', ...(isMedium ? {top: headerHeight + 24} : {})}}>
                                {[
                                    {view:'swing',    icon:<FiTrendingUp size={16}/>,  label: isMedium ? 'Wards'   : 'Target Wards'},
                                    {view:'archetype',icon:<FiUsers size={16}/>,       label:'Voters'},
                                    {view:'historic', icon:<FiBarChart2 size={16}/>,   label: isMedium ? 'Historic' : 'Historic Voting'},
                                ].map(({view,icon,label}) => (
                                    <button key={view} onClick={() => setMapView(view)}
                                        className={`flex items-center gap-2 rounded-xl font-semibold text-sm transition-all ${isMedium ? 'px-3 py-1.5' : 'px-4 py-2'} ${mapView === view ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/25' : 'text-gray-300 hover:text-white hover:bg-white/10'}`}>
                                        {icon}{label}
                                    </button>
                                ))}
                            </div>

                            <div className={`absolute ${isMedium ? '' : 'top-20'} z-10 transition-all duration-300 ease-in-out ${isFiltersOpen ? 'w-72' : 'w-auto'}`} style={{right: isMedium ? '16px' : '412px', ...(isMedium ? {top: headerHeight + 24} : {})}}>
                                <div className="backdrop-blur-md rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                                    style={{background:'rgba(10,15,28,0.82)'}}>
                                    <div onClick={() => setIsFiltersOpen(!isFiltersOpen)} className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors rounded-2xl">
                                        <div className="flex items-center gap-2">
                                            <FiFilter className={hasActiveFilters ? "text-cyan-400" : "text-gray-400"} size={15}/>
                                            <span className="font-bold text-sm text-white">Filters</span>
                                            {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"/>}
                                        </div>
                                        {isFiltersOpen ? <FiChevronUp className="text-gray-400" size={15}/> : <FiChevronDown className="text-gray-400" size={15}/>}
                                    </div>
                                    {isFiltersOpen && (
                                        <div className="px-4 pb-4 flex flex-col gap-4 border-t border-white/10">
                                            {hasActiveFilters && (
                                                <button onClick={e => { e.stopPropagation(); clearFilters(); }} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 self-end transition-colors mt-2">
                                                    <FiTrash2 size={12}/> Clear All
                                                </button>
                                            )}
                                            <div>
                                                <label className="text-xs text-gray-400 mb-1 block uppercase font-semibold">Search</label>
                                                <div className="relative">
                                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14}/>
                                                    <input type="text" placeholder="Find a ward..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 pl-9 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/40 transition-all"/>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-400 mb-1 block uppercase font-semibold">Priority Tier</label>
                                                <div className="relative">
                                                    <select value={tierFilter} onChange={e => setTierFilter(e.target.value)}
                                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500/60 transition-all">
                                                        <option value="ALL" className="bg-gray-900">All Priorities</option>
                                                        {availableTiers.map(tier => <option key={tier} value={tier} className="bg-gray-900">{tier.replace(/_/g,' ')}</option>)}
                                                    </select>
                                                    <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={14}/>
                                                </div>
                                            </div>
                                            <div className="mb-2">
                                                <label className="text-xs text-gray-400 mb-1 block uppercase font-semibold">Voter Archetype</label>
                                                <div className="relative">
                                                    <select value={archetypeFilter} onChange={e => setArchetypeFilter(e.target.value)}
                                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500/60 transition-all">
                                                        <option value="ALL" className="bg-gray-900">All Archetypes</option>
                                                        {availableArchetypes.map(arch => <option key={arch} value={arch} className="bg-gray-900">{arch}</option>)}
                                                    </select>
                                                    <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={14}/>
                                                </div>
                                            </div>
                                            {mapView === 'historic' && (
                                                <div className="mb-2">
                                                    <label className="text-xs text-gray-400 mb-1 block uppercase font-semibold">Historic Election</label>
                                                    <div className="relative">
                                                        <select value={historicElectionType} onChange={e => setHistoricElectionType(e.target.value)}
                                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500/60 transition-all">
                                                            <option value="GE24" className="bg-gray-900">General Election</option>
                                                            <option value="Local" className="bg-gray-900">Last Local Election</option>
                                                        </select>
                                                        <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={14}/>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* ═══════════════════════════════════════════════════════
                    MOBILE OVERLAY LAYER — Google Maps style
                    Everything floats over the map as absolute overlays.
                    The map always fills 100% of the container.
                ═══════════════════════════════════════════════════════ */}
                {useMobileUI && (
                    <>
                        {/* Floating status pill — sits below the top bar row (approx 48px) */}
                        <div className="absolute left-0 right-0 z-10 flex justify-center pointer-events-none" style={{top:'52px'}}>
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full shadow-xl backdrop-blur-md border border-white/10 pointer-events-auto ${mobileTierClass}`}
                                style={{background: 'rgba(17,24,39,0.82)'}}>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${mobileTierClass}`}
                                    style={{fontSize:'9px'}}>
                                    {mobileTier?.replace(/_/g, ' ')}
                                </span>
                                <span className="text-white font-semibold truncate" style={{fontSize:'12px', maxWidth:'160px'}}>
                                    {mobileTitle}
                                </span>
                            </div>
                        </div>

                        {/* Bottom handle bar — always visible, tap to expand panel */}
                        {!isPanelOpen && (
                            <div
                                className="absolute bottom-0 left-0 right-0 z-20 cursor-pointer"
                                onClick={() => setIsPanelOpen(true)}
                            >
                                <div className="mx-3 mb-3 rounded-2xl border border-white/10 shadow-2xl px-4 py-3 flex items-center justify-between"
                                    style={{background:'rgba(8,12,24,0.88)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)'}}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-2 h-2 rounded-full ${mobileTierClass.includes('orange') ? 'bg-orange-500' : mobileTierClass.includes('red') ? 'bg-red-500' : mobileTierClass.includes('green') ? 'bg-green-500' : mobileTierClass.includes('yellow') ? 'bg-yellow-500' : 'bg-cyan-500'}`}/>
                                        <div>
                                            <p className="text-white font-bold leading-none" style={{fontSize:'13px'}}>{mobileTitle}</p>
                                            <p className="text-gray-400 leading-none mt-0.5" style={{fontSize:'10px'}}>{mobileTier?.replace(/_/g, ' ')} · tap for details</p>
                                        </div>
                                    </div>
                                    <FiChevronUp className="text-gray-400" size={18}/>
                                </div>
                            </div>
                        )}

                        {/* Bottom sheet — peek (42%) by default, expands to full on demand */}
                        {isPanelOpen && (
                            <div className="absolute inset-0 z-30 flex flex-col justify-end" style={{pointerEvents:'none'}}>
                                {/* Scrim only when expanded — tap to collapse back to peek */}
                                {isPanelExpanded && (
                                    <div className="absolute inset-0" style={{background:'rgba(0,0,0,0.4)', pointerEvents:'auto'}}
                                        onClick={() => setIsPanelExpanded(false)}/>
                                )}
                                <div
                                    className="relative rounded-t-3xl border-t border-white/10 shadow-2xl flex flex-col"
                                    style={{
                                        height: isPanelExpanded ? (vh < 500 ? '88%' : '82%') : '42%',
                                        transition: 'height 0.3s cubic-bezier(0.32,0.72,0,1)',
                                        background:'rgba(8,12,24,0.95)',
                                        backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)',
                                        pointerEvents:'auto'
                                    }}
                                >
                                    {/* Handle row: X on left, drag handle centre, Expand/Collapse on right */}
                                    <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
                                        <button
                                            onClick={() => { setIsPanelOpen(false); setIsPanelExpanded(false); setSelectedWard(null); }}
                                            className="text-gray-500 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition-all">
                                            <FiX size={16}/>
                                        </button>
                                        <div className="w-10 h-1 bg-gray-600 rounded-full cursor-pointer"
                                            onClick={() => setIsPanelExpanded(!isPanelExpanded)}/>
                                        <button
                                            onClick={() => setIsPanelExpanded(!isPanelExpanded)}
                                            className="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-white px-2 py-1.5 rounded-xl hover:bg-white/10 transition-all">
                                            {isPanelExpanded ? <><FiChevronDown size={13}/><span>Collapse</span></> : <><FiChevronUp size={13}/><span>Expand</span></>}
                                        </button>
                                    </div>
                                    {/* Scrollable content */}
                                    <div className="flex-1 overflow-y-auto overscroll-contain">
                                        <InfoPanel
                                            ward={selectedWard}
                                            campaignOverview={campaignOverview}
                                            onClose={() => { setSelectedWard(null); setIsPanelOpen(false); setIsPanelExpanded(false); }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ═══════════════════════════════════════════════════════
                    MEDIUM: bottom sheet (same as mobile — map gets full width)
                ═══════════════════════════════════════════════════════ */}
                {isMedium && (
                    <>
                        {/* Bottom handle bar */}
                        {!isPanelOpen && (
                            <div className="absolute bottom-0 left-0 right-0 z-20 cursor-pointer"
                                onClick={() => setIsPanelOpen(true)}>
                                <div className="mx-4 mb-4 rounded-2xl border border-white/10 shadow-2xl px-5 py-3.5 flex items-center justify-between"
                                    style={{background:'rgba(8,12,24,0.90)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)'}}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${mobileTierClass.includes('orange') ? 'bg-orange-500' : mobileTierClass.includes('red') ? 'bg-red-500' : mobileTierClass.includes('green') ? 'bg-green-500' : mobileTierClass.includes('yellow') ? 'bg-yellow-500' : 'bg-cyan-500'}`}/>
                                        <div>
                                            <p className="text-white font-bold text-sm leading-none">{mobileTitle}</p>
                                            <p className="text-gray-400 text-xs leading-none mt-1">{mobileTier?.replace(/_/g, ' ')} · tap for intelligence</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-400">
                                        <span className="text-xs font-medium">View Details</span>
                                        <FiChevronUp size={16}/>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Bottom sheet — peek (38%) by default, expands to 78% on demand */}
                        {isPanelOpen && (
                            <div className="absolute inset-0 z-30 flex flex-col justify-end" style={{pointerEvents:'none'}}>
                                {isPanelExpanded && (
                                    <div className="absolute inset-0" style={{background:'rgba(0,0,0,0.35)', pointerEvents:'auto'}}
                                        onClick={() => setIsPanelExpanded(false)}/>
                                )}
                                <div className="relative rounded-t-3xl border-t border-white/10 shadow-2xl flex flex-col"
                                    style={{
                                        height: isPanelExpanded ? '78%' : '38%',
                                        transition: 'height 0.3s cubic-bezier(0.32,0.72,0,1)',
                                        background:'rgba(8,12,24,0.95)',
                                        backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)',
                                        pointerEvents:'auto'
                                    }}>
                                    <div className="flex items-center justify-between px-5 pt-3.5 pb-2 shrink-0">
                                        <button onClick={() => { setIsPanelOpen(false); setIsPanelExpanded(false); setSelectedWard(null); }}
                                            className="text-gray-500 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition-all">
                                            <FiX size={16}/>
                                        </button>
                                        <div className="w-10 h-1 bg-gray-600 rounded-full cursor-pointer"
                                            onClick={() => setIsPanelExpanded(!isPanelExpanded)}/>
                                        <button onClick={() => setIsPanelExpanded(!isPanelExpanded)}
                                            className="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-white px-2 py-1.5 rounded-xl hover:bg-white/10 transition-all">
                                            {isPanelExpanded ? <><FiChevronDown size={13}/><span>Collapse</span></> : <><FiChevronUp size={13}/><span>Expand</span></>}
                                        </button>
                                    </div>
                                    <div className="flex-1 overflow-y-auto overscroll-contain">
                                        <InfoPanel ward={selectedWard} campaignOverview={campaignOverview}
                                            onClose={() => { setSelectedWard(null); setIsPanelOpen(false); setIsPanelExpanded(false); }}/>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ═══════════════════════════════════════════════════════
                    LARGE DESKTOP SIDEBAR — full persistent panel
                ═══════════════════════════════════════════════════════ */}
                {!useMobileUI && !isMedium && (
                    <div className="absolute top-20 right-4 bottom-4 z-10 flex flex-col" style={{width:'380px'}}>
                        <div className="flex-1 rounded-3xl border border-white/10 shadow-2xl overflow-hidden text-white flex flex-col"
                            style={{background:'rgba(10,15,28,0.82)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)'}}>
                            <InfoPanel 
                                ward={selectedWard} 
                                campaignOverview={campaignOverview} 
                                onClose={() => setSelectedWard(null)} 
                            />
                        </div>
                    </div>
                )}
            </div>

            <ArchetypeModal 
                isOpen={modalState.isOpen} 
                onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))} 
                data={modalState.data}
                wardName={modalState.wardName}
            />
        </div>
    );
}